import { create } from "zustand";
import { persist } from "zustand/middleware";

import type { ThemeMode } from "@/theme/muiTheme";

/**
 * 用戶端 UI 狀態。
 *
 * **只放純前端偏好** —— 側欄開合、深淺色。
 * 任何來自後端的資料一律交給 React Query，不要放進這裡，
 * 否則會出現兩份不同步的伺服器狀態。
 */
interface UiState {
  themeMode: ThemeMode;
  /** 服務 rail 旁邊那層「有中文標籤」的面板是否展開 */
  railExpanded: boolean;
  setThemeMode: (mode: ThemeMode) => void;
  toggleRail: () => void;
  setRailExpanded: (expanded: boolean) => void;
}

/** 舊版（v1）的形狀，只用於 migrate。 */
interface UiStateV1 {
  themeMode?: ThemeMode;
  navOpen?: boolean;
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      themeMode: "system",
      railExpanded: true,
      setThemeMode: (themeMode) => set({ themeMode }),
      toggleRail: () => set((state) => ({ railExpanded: !state.railExpanded })),
      setRailExpanded: (railExpanded) => set({ railExpanded }),
    }),
    {
      name: "ui-preferences",
      version: 2,
      /**
       * v1 把這個狀態叫 navOpen（那時候側欄是一整條 260px 的抽屜）。
       * 改成 icon rail + 可展開第二層之後語意變了，但使用者的偏好值可以沿用，
       * 所以在這裡搬過去而不是讓它靜靜地重設成預設值。
       */
      migrate: (persisted, version) => {
        if (version >= 2) {
          return persisted as UiState;
        }
        const old = (persisted ?? {}) as UiStateV1;
        return {
          themeMode: old.themeMode ?? "system",
          railExpanded: old.navOpen ?? true,
        } as UiState;
      },
    },
  ),
);
